import { Link, useNavigate } from "react-router-dom";
import { Heart, Eye, ShoppingBag, Star } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { imgUrl, rupee } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const ProductCard = ({ product, onQuickView }) => {
  const { addToCart, toggleWishlist, inWishlist } = useStore();
  const navigate = useNavigate();
  const discount = product.mrp > product.price ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;
  const wished = inWishlist(product.id);

  return (
    <div className="group relative flex flex-col" data-testid={`product-card-${product.slug}`}>
      <div className="relative overflow-hidden bg-muted aspect-[3/4]">
        <Link to={`/product/${product.slug}`} aria-label={product.name}>
          <img
            src={imgUrl(product.primary_image)}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        {discount > 0 && (
          <span className="absolute left-0 top-3 bg-burgundy px-2.5 py-1 text-xs font-medium text-white">
            {discount}% OFF
          </span>
        )}
        <button
          onClick={() => toggleWishlist(product)}
          data-testid={`wishlist-btn-${product.slug}`}
          aria-label="Toggle wishlist"
          className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-white/90 text-charcoal shadow-sm transition-colors hover:bg-white"
        >
          <Heart className={`h-4 w-4 ${wished ? "fill-burgundy text-burgundy" : ""}`} />
        </button>
        <div className="absolute inset-x-0 bottom-0 flex translate-y-full gap-2 p-2 transition-transform duration-300 group-hover:translate-y-0">
          <Button
            size="sm"
            onClick={() => addToCart(product)}
            data-testid={`add-cart-${product.slug}`}
            className="flex-1 rounded-none bg-burgundy text-xs uppercase tracking-wider hover:bg-burgundy/90"
          >
            <ShoppingBag className="mr-1.5 h-3.5 w-3.5" /> Add
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => (onQuickView ? onQuickView(product) : navigate(`/product/${product.slug}`))}
            data-testid={`quickview-${product.slug}`}
            className="rounded-none border-charcoal/20 bg-white text-charcoal hover:bg-cream"
          >
            <Eye className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <div className="pt-3">
        <p className="text-[11px] uppercase tracking-widest text-muted-text">{product.category}</p>
        <Link to={`/product/${product.slug}`} className="mt-1 block font-sans text-sm font-medium text-charcoal hover:text-burgundy">
          {product.name}
        </Link>
        <div className="mt-1 flex items-center gap-1 text-xs text-muted-text">
          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
          <span>{product.rating}</span>
          <span className="text-muted-text/60">({product.reviews_count || 0})</span>
        </div>
        <div className="mt-1.5 flex items-baseline gap-2">
          <span className="font-sans text-sm font-semibold text-burgundy">{rupee(product.price)}</span>
          {product.mrp > product.price && (
            <span className="text-xs text-muted-text line-through">{rupee(product.mrp)}</span>
          )}
        </div>
      </div>
    </div>
  );
};
